import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, X } from "lucide-react";

// Sticky top strip that surfaces the user's remaining 14-day Pro trial.
// Lives in DashboardLayout above the header so it's visible across the app.
// Dismissable per-session (sessionStorage); always reappears on reload until
// the trial ends or the user subscribes.
const DISMISS_KEY = "trial_banner_dismissed_v1";

export default function TrialBanner() {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.sessionStorage.getItem(DISMISS_KEY) === "1";
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/subscription/me");
        if (!cancelled) setSub(data);
      } catch (e) {
        // Unauthenticated or network issue — banner stays hidden. Surface in
        // console for dev debugging without disrupting the user.
        console.debug("TrialBanner subscription probe failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!sub) return null;
  if (sub.free_pass_active) return null; // launch promo: everything free; trial banner is misleading
  if (sub.is_admin) return null;         // admins have permanent access — no banner
  if (sub.active) return null;           // paid users — no banner
  if (!sub.trial_active) return null;    // trial expired — no banner (Subscription page covers upsell)
  if (dismissed) return null;

  const days = sub.trial_days_left;
  const dismiss = () => {
    try { window.sessionStorage.setItem(DISMISS_KEY, "1"); }
    catch (e) { console.debug("TrialBanner: sessionStorage write failed (private browsing?)", e); }
    setDismissed(true);
  };

  return (
    <div
      className="w-full bg-primary text-primary-foreground"
      data-testid="trial-banner"
      role="status"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2.5 flex items-center gap-3 flex-wrap">
        <Sparkles size={16} className="shrink-0" />
        <p className="text-sm flex-1 min-w-0">
          <span className="font-medium">{days} {days === 1 ? "day" : "days"} left in your Pro trial.</span>
          <span className="opacity-90 ml-1.5 hidden sm:inline">
            All features unlocked — no credit card required.
          </span>
        </p>
        <Link
          to="/subscription"
          data-testid="trial-banner-cta"
          className="text-sm font-medium underline underline-offset-4 hover:opacity-90"
        >
          See plans →
        </Link>
        <button
          type="button"
          onClick={dismiss}
          data-testid="trial-banner-dismiss"
          aria-label="Dismiss trial banner"
          className="p-1 rounded hover:bg-primary-foreground/10 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
